import {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  MouseEventHandler,
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { IconFCType } from '@ui/icons/types'

import { useCopy } from '@hooks/useCopy'

import { Box } from '@shared/components/Box'

import { BaseField, BaseFieldProps, LabelConstraint } from './BaseField/BaseField'

type BaseInputAttributes = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'placeholder'>

type BaseFieldConfig = Pick<
  BaseFieldProps,
  | 'size'
  | 'variant'
  | 'labelSupportText'
  | 'hint'
  | 'hintSupportText'
  | 'hintIcon'
  | 'rightText'
  | 'rightContent'
  | 'renderRightIconJSX'
  | 'disabled'
  | 'error'
  | 'readOnly'
  | 'leftText'
  | 'onStartIconClick'
  | 'onEndIconClick'
  | 'buttonComponent'
  | 'fullWidth'
  | 'focused'
>

export type InputProps = BaseInputAttributes &
  BaseFieldConfig &
  LabelConstraint & {
    onWrapperClick?: MouseEventHandler<HTMLDivElement>
    leftIcon?: IconFCType
    rightIcon?: IconFCType
    decimalPlaces?: number
    maxDigits?: number
    zerosLimitter?: boolean
    lockOnLimit?: boolean
    min?: number
    max?: number
    renderRightIconJSX?: React.ReactNode
    boxRef?: React.RefObject<HTMLDivElement | null>
    showLabel?: boolean
    canCopy?: boolean
  }

export const Input = memo(
  forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    const {
      label,
      onChange,
      onFocus,
      onBlur,
      onWrapperClick,
      onStartIconClick,
      onEndIconClick,
      decimalPlaces,
      maxDigits,
      zerosLimitter,
      lockOnLimit,
      min,
      max,
      type = 'text',
      disabled,
      readOnly,
      value,
      placeholder = label,
      size,
      variant,
      labelSupportText,
      hint,
      hintSupportText,
      hintIcon,
      leftText,
      leftIcon,
      rightText,
      rightIcon,
      rightContent,
      error,
      buttonComponent,
      renderRightIconJSX,
      onClick,
      fullWidth,
      focused,
      boxRef,
      showLabel,
      canCopy,
      name,
      required,
      autoComplete,
      onKeyDown,
      onKeyUp,
      ...restProps
    } = props
    const { copy } = useCopy()
    const { t } = useTranslation()
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [])

    const [isFocused, setIsFocused] = useState(false)
    const [hasValue, setHasValue] = useState(() => {
      return !!(value !== undefined && value !== '' ? value : restProps.defaultValue)
    })

    const maxDecimalPlaces = useMemo(() => {
      if (decimalPlaces === undefined) return null
      return new RegExp(`^\\d*\\.?\\d{0,${decimalPlaces}}$`)
    }, [decimalPlaces])

    const startsWithMultipleZeros = useMemo(() => new RegExp(`^0{2,}`), [])

    const validateNumberInput = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (decimalPlaces !== undefined && maxDecimalPlaces) {
          if (zerosLimitter) {
            if (startsWithMultipleZeros.test(e.target.value)) return
          }
          if (!maxDecimalPlaces.test(e.target.value)) return
        } else if (zerosLimitter) {
          if (startsWithMultipleZeros.test(e.target.value)) return
        }

        if (maxDigits !== undefined) {
          if (e.target.value.split('.')[0].length > maxDigits) return
        }

        if (isNaN(parseFloat(e.target.value))) {
          return onChange?.(e)
        }

        const numberValue = parseFloat(e.target.value)

        if (min !== undefined && numberValue < min) {
          if (lockOnLimit) return
          return onChange?.({ ...e, target: { ...e.target, value: min.toString() } })
        }

        if (max !== undefined && numberValue > max) {
          if (lockOnLimit) return
          return onChange?.({ ...e, target: { ...e.target, value: max.toString() } })
        }

        return onChange?.(e)
      },
      [
        decimalPlaces,
        maxDecimalPlaces,
        zerosLimitter,
        startsWithMultipleZeros,
        maxDigits,
        onChange,
        min,
        lockOnLimit,
        max,
      ]
    )

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (disabled) return
        if (value === undefined) setHasValue(!!e.target.value)
        if (type === 'number') return validateNumberInput(e)

        return onChange?.(e)
      },
      [disabled, value, type, validateNumberInput, onChange]
    )

    const handleWrapperClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
      (event) => {
        if (disabled) return
        inputRef.current?.focus()
        if (onWrapperClick) {
          onWrapperClick(event)
        } else {
          onClick?.(event as any)
        }
      },
      [disabled, onWrapperClick, onClick]
    )

    const handleStartIconClick: React.MouseEventHandler<HTMLSpanElement> = useCallback(
      (event) => {
        if (disabled) return
        if (onStartIconClick) {
          event.stopPropagation()
          onStartIconClick(event)
        } else if (onClick) {
          event.stopPropagation()
          onClick(event as any)
        }
      },
      [disabled, onStartIconClick, onClick]
    )

    const handleEndIconClick: React.MouseEventHandler<HTMLSpanElement> = useCallback(
      (event) => {
        if (disabled) return

        if (onEndIconClick) {
          event.stopPropagation()
          onEndIconClick(event)
        } else if (onClick) {
          event.stopPropagation()
          onClick(event as any)
        }
      },
      [disabled, onEndIconClick, onClick]
    )

    const handleFocus = useCallback(
      (event: FocusEvent<HTMLInputElement, Element>) => {
        setIsFocused(true)
        onFocus?.(event)
      },
      [onFocus]
    )

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLInputElement, Element>) => {
        setIsFocused(false)
        if (type !== 'password' && event.target.value) {
          const trimmed = event.target.value.trim()
          const changed = trimmed !== event.target.value
          event.target.value = trimmed
          if (changed) onChange?.(event as unknown as ChangeEvent<HTMLInputElement>)
        }
        onBlur?.(event)
      },
      [type, onChange, onBlur]
    )

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && type !== 'password' && e.currentTarget.value) {
          const trimmed = e.currentTarget.value.trim()
          const changed = trimmed !== e.currentTarget.value
          e.currentTarget.value = trimmed
          if (changed) onChange?.(e as unknown as ChangeEvent<HTMLInputElement>)
        }
        onKeyDown?.(e)
      },
      [type, onChange, onKeyDown]
    )

    const willShowLabel = showLabel !== false

    const isControlled = value !== undefined
    const actuallyHasValue = isControlled ? !!value : hasValue

    const isPlaceholderMatchingLabel =
      willShowLabel && !!label && !!placeholder && placeholder.trim() === label.trim()

    const isEffectivelyFocused = focused ?? isFocused

    const useFloatingLabel =
      willShowLabel && !actuallyHasValue && !isEffectivelyFocused && isPlaceholderMatchingLabel

    const resolvedPlaceholder = isPlaceholderMatchingLabel ? ' ' : placeholder

    return (
      <Box ref={boxRef} fullWidth={fullWidth}>
        <BaseField
          size={size}
          variant={variant}
          label={willShowLabel ? label : ''}
          isLabelActingAsPlaceholder={useFloatingLabel}
          labelSupportText={labelSupportText}
          hint={hint}
          hintSupportText={hintSupportText}
          hintIcon={hintIcon}
          leftText={leftText}
          rightText={rightText}
          disabled={disabled}
          error={error}
          handleCopy={canCopy ? () => copy(value?.toString(), t('Copied')) : undefined}
          readOnly={readOnly}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          renderRightIconJSX={renderRightIconJSX}
          onStartIconClick={handleStartIconClick}
          onEndIconClick={handleEndIconClick}
          rightContent={rightContent}
          onWrapperClick={handleWrapperClick}
          buttonComponent={buttonComponent}
          fullWidth={fullWidth}
          focused={focused}
        >
          <input
            {...restProps}
            ref={inputRef}
            name={name}
            type={type}
            value={value}
            placeholder={resolvedPlaceholder}
            min={min !== undefined && !isNaN(min) ? min : undefined}
            max={max !== undefined && !isNaN(max) ? max : undefined}
            disabled={disabled}
            readOnly={readOnly}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onKeyUp={onKeyUp}
            required={required}
            autoComplete={autoComplete}
            data-placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </BaseField>
      </Box>
    )
  })
)
